// Чистый UI-компонент панели студента: приветствие, четыре карточки навигации.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Modal, Button, Spinner } from 'react-bootstrap';
import { getStudentExerciseTypes } from '../../api/studentApi';

/**
 * Иконка "книга" — для лого в шапке и карточки "Выбрать упражнения"
 */
function IconBook({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

/**
 * Иконка "столбчатая диаграмма" — для карточки "Моя статистика"
 */
function IconBarChart({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
      <line x1="2" y1="20" x2="22" y2="20" />
    </svg>
  );
}

/**
 * Иконка "play" — для карточки "Продолжить"
 */
function IconPlay({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

/**
 * Цветной квадратный icon-box с градиентным фоном.
 */
function IconBox({ gradient, children }) {
  return (
    <div
      style={{
        width: '3.5rem',
        height: '3.5rem',
        borderRadius: '0.875rem',
        background: gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Иконка "повторить" (круговая стрелка)
 */
function IconRepeat({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 .49-3.51" />
    </svg>
  );
}

/**
 * Одна карточка дашборда.
 *
 * @param {{ iconBox, title, description, ctaText, ctaHref, ctaDisabled }} props
 */
function DashboardCard({ iconBox, title, description, ctaText, ctaHref, ctaDisabled }) {
  const navigate = useNavigate();

  return (
    <Card
      className="border-0 h-100"
      style={{
        borderRadius: '1rem',
        background: '#fff',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
      }}
    >
      <Card.Body className="p-4 d-flex flex-column">
        <div
          className="mb-3"
          onClick={() => !ctaDisabled && ctaHref && navigate(ctaHref)}
          style={{ cursor: ctaDisabled ? 'default' : 'pointer', display: 'inline-block' }}
        >
          {iconBox}
        </div>

        <h5
          className="fw-bold mb-2"
          style={{ color: '#1e2a4a' }}
        >
          {title}
        </h5>

        <p
          className="mb-4"
          style={{
            color: '#6c757d',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            flex: 1,
          }}
        >
          {description}
        </p>

        {ctaDisabled ? (
          <span
            style={{
              color: '#adb5bd',
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            {ctaText}
          </span>
        ) : (
          <a
            href={ctaHref}
            onClick={(e) => {
              e.preventDefault();
              navigate(ctaHref);
            }}
            style={{
              color: '#4f6ef7',
              fontWeight: 600,
              fontSize: '0.875rem',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
          >
            {ctaText}
          </a>
        )}
      </Card.Body>
    </Card>
  );
}

/**
 * StudentDashboard — чистый UI-компонент панели студента.
 *
 * @param {{
 *   user: object,
 *   studentLevel: string | null,
 *   incompleteCount: number,
 *   loadingCount: boolean,
 * }} props
 */
export default function StudentDashboard({ user, studentLevel, incompleteCount, loadingCount, isGuest = false }) {
  const navigate = useNavigate();

  const incompleteCta = loadingCount
    ? { text: 'Загрузка...', disabled: true }
    : incompleteCount > 0
    ? { text: `${incompleteCount} незавершённых упражнений`, disabled: false, href: '/student/incomplete' }
    : { text: 'Нет незавершённых', disabled: true };

  const [showRetryModal, setShowRetryModal] = useState(false);
  const [exerciseTypes, setExerciseTypes]   = useState([]);
  const [loadingTypes, setLoadingTypes]     = useState(false);

  const openRetryModal = async () => {
    setShowRetryModal(true);
    setLoadingTypes(true);
    try {
      const { data } = await getStudentExerciseTypes();
      setExerciseTypes(data ?? []);
    } catch {
      setExerciseTypes([]);
    } finally {
      setLoadingTypes(false);
    }
  };

  return (
    <div style={{ background: 'var(--ewl-bg-body, #f8faff)', minHeight: 'calc(100vh - 56px)' }}>

      {/* ── Основной контент ── */}
      <Container style={{ maxWidth: '900px' }} className="py-5">

        {/* Приветственный блок */}
        <div className="text-center mb-5">
          <h1
            className="fw-bold mb-3"
            style={{
              fontSize: 'clamp(2rem, 5vw, 2.75rem)',
              background: 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: 1.2,
            }}
          >
            Добро пожаловать!
          </h1>
          <p
            style={{
              color: '#6c757d',
              fontSize: '1rem',
              maxWidth: '480px',
              margin: '0 auto',
              lineHeight: 1.6,
            }}
          >
            {isGuest ? (
              <>
                <Link to="/register" style={{ color: '#4f6ef7', fontWeight: 600 }}>
                  Зарегистрируйтесь
                </Link>
                {' '}для доступа ко всем возможностям!
              </>
            ) : (
              'Выберите, как вы хотите продолжить обучение сегодня'
            )}
          </p>
        </div>

        {/* Четыре карточки */}
        <Row className="g-4">

          {/* Ряд 1, Карточка 1: Выбрать упражнения */}
          <Col xs={12} md={6}>
            <DashboardCard
              iconBox={
                <IconBox gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)">
                  <IconBook size={20} color="#fff" />
                </IconBox>
              }
              title="Выбрать упражнения"
              description="Изучайте новые темы и выбирайте упражнения по своему желанию"
              ctaText="Обзор всех упражнений →"
              ctaHref="/student/exercises"
              ctaDisabled={false}
            />
          </Col>

          {/* Ряд 1, Карточка 2: Продолжить */}
          <Col xs={12} md={6}>
            <DashboardCard
              iconBox={
                <IconBox gradient="linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)">
                  <IconPlay size={20} color="#fff" />
                </IconBox>
              }
              title="Продолжить"
              description="Завершите начатые упражнения и улучшите свои результаты"
              ctaText={isGuest ? 'Только для зарегистрированных' : incompleteCta.text}
              ctaHref={isGuest ? undefined : incompleteCta.href}
              ctaDisabled={isGuest || incompleteCta.disabled}
            />
          </Col>

          {/* Ряд 2, Карточка 1: Повторить */}
          <Col xs={12} md={6}>
            <Card
              className="border-0 h-100"
              style={{
                borderRadius: '1rem',
                background: '#fff',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                transition: 'transform 0.18s ease, box-shadow 0.18s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
              }}
            >
              <Card.Body className="p-4 d-flex flex-column">
                <div
                  className="mb-3"
                  onClick={() => !isGuest && openRetryModal()}
                  style={{ cursor: isGuest ? 'default' : 'pointer', display: 'inline-block' }}
                >
                  <IconBox gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
                    <IconRepeat size={20} color="#fff" />
                  </IconBox>
                </div>
                <h5 className="fw-bold mb-2" style={{ color: '#1e2a4a' }}>
                  Повторить
                </h5>
                <p
                  className="mb-4"
                  style={{ color: '#6c757d', fontSize: '0.875rem', lineHeight: 1.6, flex: 1 }}
                >
                  Повторить задания, в которых возникли трудности.
                </p>
                {isGuest ? (
                  <span style={{ color: '#adb5bd', fontWeight: 600, fontSize: '0.875rem' }}>
                    Только для зарегистрированных
                  </span>
                ) : (
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); openRetryModal(); }}
                    style={{
                      color: '#f59e0b',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                  >
                    Выбрать тип →
                  </a>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Ряд 2, Карточка 2: Моя статистика */}
          <Col xs={12} md={6}>
            <DashboardCard
              iconBox={
                <IconBox gradient="linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)">
                  <IconBarChart size={22} color="#fff" />
                </IconBox>
              }
              title="Моя статистика"
              description="Посмотрите свой прогресс и получите персональные рекомендации"
              ctaText={isGuest ? 'Только для зарегистрированных' : 'Открыть статистику →'}
              ctaHref={isGuest ? undefined : '/student/statistics'}
              ctaDisabled={isGuest}
            />
          </Col>

        </Row>
      </Container>

      {/* ── Modal выбора типа упражнения ── */}
      <Modal show={showRetryModal} onHide={() => setShowRetryModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>
            Выберите тип упражнения
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.25rem 1.5rem' }}>
          {loadingTypes ? (
            <div className="text-center py-3">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : exerciseTypes.length === 0 ? (
            <p className="text-muted text-center py-2 mb-0">Типы упражнений не найдены</p>
          ) : (
            <div className="d-flex flex-column gap-2">
              {exerciseTypes.map((type) => (
                <Button
                  key={type.id}
                  variant="outline-primary"
                  onClick={() => {
                    setShowRetryModal(false);
                    navigate('/student/retry-errors', {
                      state: { typeId: type.id, typeName: type.name },
                    });
                  }}
                  style={{ borderRadius: '0.6rem', fontWeight: 500, textAlign: 'left' }}
                >
                  {type.name}
                </Button>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>

    </div>
  );
}
